import { useId, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError, toApiError, unwrap } from "../api/client";
import { t, type MessageKey } from "../i18n/t";
import { routes } from "../lib/routes";
import { ErrorMessage } from "./ui";

const ROLES: { value: string; label: MessageKey }[] = [
  { value: "Trafikksikkerhetskoordinator", label: "contact.roleCoordinator" },
  { value: "Skole eller oppvekst", label: "contact.roleSchool" },
  { value: "Teknisk/samferdsel", label: "contact.roleTechnical" },
  { value: "FAU", label: "contact.roleFau" },
  { value: "Rådgiver eller konsulent", label: "contact.roleConsultant" },
  { value: "Annet", label: "contact.roleOther" },
];

function Required() {
  return (
    <span aria-hidden="true" className="required-mark">
      {" "}
      *
    </span>
  );
}

/** The public contact form ("Bestill gjennomgang"). `?kommune=NNNN` ties the request to a kommune. */
export function LeadForm() {
  const id = useId();
  const [params] = useSearchParams();
  const kommune = /^\d{4}$/.test(params.get("kommune") ?? "") ? params.get("kommune") : null;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.POST("/api/public/leads", {
          body: {
            name: name.trim(),
            email: email.trim(),
            organisation: organisation.trim(),
            role: role || null,
            kommunenummer: kommune,
            message: message.trim() || null,
            source: kommune ? `kommune:${kommune}` : "kontakt",
            website,
          },
        }),
      );
      setDone(true);
    } catch (err) {
      const failure = toApiError(err);
      // The server's own text for "too many today"; anything else is a generic retry message.
      setError(failure.status === 429 ? failure : new ApiError(failure.status, t("contact.error")));
    } finally {
      setBusy(false);
    }
  }

  if (done)
    return (
      <section className="card" role="status">
        <h2>{t("contact.successTitle")}</h2>
        <p>{t("contact.successBody")}</p>
      </section>
    );

  return (
    <form className="card form" onSubmit={(e) => void submit(e)}>
      {kommune && <p className="hint">{t("contact.kommuneLine", { nr: kommune })}</p>}
      <div className="field">
        <label htmlFor={`${id}-name`}>
          {t("contact.name")}
          <Required />
        </label>
        <input
          id={`${id}-name`}
          name="name"
          required
          maxLength={200}
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor={`${id}-email`}>
          {t("contact.email")}
          <Required />
        </label>
        <input
          id={`${id}-email`}
          name="email"
          type="email"
          required
          maxLength={320}
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor={`${id}-organisation`}>
          {t("contact.organisation")}
          <Required />
        </label>
        <input
          id={`${id}-organisation`}
          name="organisation"
          required
          maxLength={200}
          autoComplete="organization"
          value={organisation}
          onChange={(e) => setOrganisation(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor={`${id}-role`}>{t("contact.role")}</label>
        <select
          id={`${id}-role`}
          name="role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="">{t("contact.rolePlaceholder")}</option>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {t(r.label)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`${id}-message`}>{t("contact.message")}</label>
        <textarea
          id={`${id}-message`}
          name="message"
          rows={5}
          maxLength={2000}
          aria-describedby={`${id}-message-hint`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <p id={`${id}-message-hint`} className="hint">
          {t("contact.messageHint")}
        </p>
      </div>
      <div className="fau-honeypot" aria-hidden="true">
        <label htmlFor={`${id}-website`}>{t("contact.honeypot")}</label>
        <input
          id={`${id}-website`}
          name="website"
          value={website}
          tabIndex={-1}
          autoComplete="off"
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
      <p className="hint">
        {t("contact.privacy")} <Link to={routes.privacy()}>{t("contact.privacyLink")}</Link>
      </p>
      {error && <ErrorMessage error={error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {t(busy ? "contact.sending" : "contact.submit")}
        </button>
      </div>
    </form>
  );
}
